import React, { useState, useRef } from 'react';
import { Recipe, Ingredient, User } from '../types';
import { generateRecipeImage } from '../services/geminiService';

interface CoachScreenProps {
  user: User | null;
  existingRecipes: Recipe[];
  onAddRecipe: (recipe: Recipe) => void;
  onSelectRecipe: (id: string) => void;
  onShowNotification: (msg: string) => void;
  
  // Lifted State Props
  inventory: string;
  setInventory: (val: string) => void;
  isAnalyzing: boolean;
  recommendation: any;
  setRecommendation: (rec: any) => void; // Optioneel als we direct manipuleren
  addedIngredients: string[];
  isRefining: boolean;
  
  // Actions
  onAnalyze: () => void;
  onPhotoAnalyze: (base64: string) => void;
  onRefine: (prompt: string) => void;
  onAddIngredient: (ing: Ingredient) => void;
}

export const CoachScreen: React.FC<CoachScreenProps> = ({
  user,
  existingRecipes,
  onAddRecipe,
  onSelectRecipe,
  onShowNotification,
  inventory,
  setInventory,
  isAnalyzing,
  recommendation,
  addedIngredients,
  isRefining,
  onAnalyze,
  onPhotoAnalyze,
  onRefine,
  onAddIngredient
}) => {
  const [isPreparingRecipe, setIsPreparingRecipe] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");
  
  // Camera States
  const [isScanning, setIsScanning] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [showShutterEffect, setShowShutterEffect] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
      try {
          setIsScanning(true);
          setIsCameraReady(false);
          
          let stream;
          try {
              // Poging 1: Hoge kwaliteit (4K)
              stream = await navigator.mediaDevices.getUserMedia({ 
                  video: { 
                      facingMode: 'environment',
                      width: { ideal: 4096 }, 
                      height: { ideal: 2160 } 
                  } 
              });
          } catch (highResError) {
              console.warn("High res camera failed, falling back to default", highResError);
              // Poging 2: Fallback naar default resolutie
              stream = await navigator.mediaDevices.getUserMedia({ 
                  video: { facingMode: 'environment' } 
              });
          }

          streamRef.current = stream;
          
          if(videoRef.current) {
              videoRef.current.srcObject = stream;
              // Zorg dat video niet gemuted is door autoplay policies (hoewel muted attribuut helpt)
              videoRef.current.muted = true; 
              videoRef.current.play().catch(e => console.error("Play error:", e));
          }

          // Check voor flitser support
          const track = stream.getVideoTracks()[0];
          const capabilities = track.getCapabilities ? track.getCapabilities() : {};
          // @ts-ignore
          if (capabilities.torch) {
              setHasFlash(true);
          }
          
      } catch(e) {
          onShowNotification("Geen camera toegang. Controleer je instellingen.");
          setIsScanning(false);
          console.error(e);
      }
  };

  const stopCamera = () => {
      if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
      }
      setIsScanning(false);
      setFlashOn(false);
      setIsCameraReady(false);
  };

  const toggleFlash = async () => {
      if (!streamRef.current || !hasFlash) return;
      const track = streamRef.current.getVideoTracks()[0];
      try {
          await track.applyConstraints({
              advanced: [{ torch: !flashOn }] as any
          });
          setFlashOn(!flashOn);
      } catch (e) {
          console.error("Flash error", e);
          onShowNotification("Kan flitser niet bedienen.");
      }
  };

  const takePicture = async () => {
      if (!videoRef.current || !canvasRef.current || isCapturing || !isCameraReady) return;

      setIsCapturing(true);
      setShowShutterEffect(true);
      
      // Korte animatie timer
      setTimeout(() => setShowShutterEffect(false), 150);

      try {
          const canvas = canvasRef.current;
          canvas.width = videoRef.current.videoWidth;
          canvas.height = videoRef.current.videoHeight;
          
          const ctx = canvas.getContext('2d');
          if (ctx) {
              ctx.drawImage(videoRef.current, 0, 0);
              // Hoge kwaliteit capture (compressie gebeurt in service)
              const base64 = canvas.toDataURL('image/jpeg', 0.95);
              
              // Sluit camera direct voor performance
              stopCamera();
              
              onShowNotification("Foto gemaakt! Analyseren...");
              onPhotoAnalyze(base64);
          }
      } catch (e) {
          console.error("Capture error", e);
          onShowNotification("Fout bij maken foto. Probeer opnieuw.");
          stopCamera();
      } finally {
          setIsCapturing(false);
      }
  };

  const handleSaveAndViewNewRecipe = async () => {
    if (!recommendation?.newRecipe) return;
    setIsPreparingRecipe(true);
    const newR = recommendation.newRecipe;
    
    try {
        onShowNotification("Smakelijke foto wordt gegenereerd...");
        const aiImg = await generateRecipeImage(newR.title);
        
        const finalRecipe: Recipe = { 
            ...newR,
            id: `recipe-${Date.now()}`, 
            image: aiImg || newR.image, 
            isFavorite: false, 
            status: 'ready' as const 
        };
        
        await onAddRecipe(finalRecipe);
        
        setTimeout(() => {
            onSelectRecipe(finalRecipe.id);
            setIsPreparingRecipe(false);
            onShowNotification("Recept opgeslagen in kookboek!");
        }, 400);
    } catch (err) {
        onShowNotification("Fout bij opslaan recept.");
        setIsPreparingRecipe(false);
    }
  };

  if (!user) {
      return (
        <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark overflow-y-auto no-scrollbar pb-32">
             <header className="flex-none z-10 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm px-4 pt-6 pb-2 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="tracking-tight text-xl font-bold leading-tight">Koelkast Coach</h1>
                    <p className="text-[10px] text-gray-400 -mt-0.5">Samen tegen voedselverspilling</p>
                  </div>
                  <div className="flex gap-1">
                    <span className="material-symbols-outlined text-primary text-[24px]">kitchen</span>
                  </div>
                </div>
              </header>
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-6 opacity-60">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-4xl text-gray-400">lock</span>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold mb-2">Login vereist</h2>
                        <p className="text-sm text-gray-500 max-w-[250px] mx-auto">De Koelkast Coach gebruikt geavanceerde AI om recepten voor je te bedenken. Log in via Instellingen om dit te gebruiken.</p>
                    </div>
                    <div className="flex flex-col items-center gap-2 mt-4">
                        <span className="text-xs text-primary font-bold">Ga naar Opties</span>
                        <span className="material-symbols-outlined text-primary animate-bounce">arrow_downward</span>
                    </div>
              </div>
        </div>
      );
  }

  // Camera Overlay
  if (isScanning) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex flex-col">
        <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted
            onCanPlay={() => setIsCameraReady(true)}
            className="w-full h-full object-cover" 
        />
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Shutter Flash Effect */}
        {showShutterEffect && (
            <div className="absolute inset-0 bg-white z-50 animate-out fade-out duration-150"></div>
        )}

        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-10 bg-gradient-to-b from-black/60 to-transparent pb-12">
             <button onClick={stopCamera} className="bg-black/40 p-3 rounded-full text-white backdrop-blur-sm hover:bg-white/20 transition">
                <span className="material-symbols-outlined">close</span>
             </button>
             {hasFlash && (
                 <button onClick={toggleFlash} className={`p-3 rounded-full backdrop-blur-sm transition-colors ${flashOn ? 'bg-yellow-400 text-black' : 'bg-black/40 text-white'}`}>
                     <span className="material-symbols-outlined">{flashOn ? 'flash_on' : 'flash_off'}</span>
                 </button>
             )}
        </div>

        {/* Bottom Controls */}
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pb-12 pt-24 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
             {!isCameraReady ? (
                 <div className="mb-8 flex items-center gap-2 text-white">
                     <span className="material-symbols-outlined animate-spin">sync</span>
                     <span className="text-sm font-bold">Camera starten...</span>
                 </div>
             ) : (
                 <>
                    <div className="mb-6 bg-black/40 px-4 py-2 rounded-full text-white text-xs font-bold backdrop-blur-sm border border-white/10 flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm text-primary">auto_fix_high</span>
                        Gemini Vision
                    </div>
                    
                    <button 
                        onClick={takePicture} 
                        disabled={isCapturing}
                        className={`h-20 w-20 rounded-full border-4 border-white p-1 transition-all duration-200 ${isCapturing ? 'scale-90 opacity-50 cursor-wait' : 'active:scale-95 hover:scale-105'}`}
                    >
                        <div className="w-full h-full bg-white rounded-full"></div>
                    </button>
                 </>
             )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark overflow-y-auto no-scrollbar pb-32">
      {isPreparingRecipe && (
          <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-300">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-2">Coach is aan het koken...</h2>
              <p className="text-gray-400 text-sm">We maken de foto en slaan het recept voor je op in je kookboek.</p>
          </div>
      )}

      <header className="flex-none z-10 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm px-4 pt-6 pb-2 border-b border-gray-100 dark:border-white/5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="tracking-tight text-xl font-bold leading-tight">Koelkast Coach</h1>
            <p className="text-[10px] text-gray-400 -mt-0.5">Samen tegen voedselverspilling</p>
          </div>
          <div className="flex gap-1">
            <span className="material-symbols-outlined text-primary text-[24px]">kitchen</span>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">
        <section className="bg-surface-light dark:bg-surface-dark rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-primary text-3xl">restaurant_menu</span>
                <h2 className="text-lg font-bold">Wat ligt er nog?</h2>
            </div>
            <button 
                onClick={startCamera}
                className="p-2 bg-gray-100 dark:bg-white/10 rounded-full text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-black transition-colors group"
                title="Maak foto"
            >
                <span className="material-symbols-outlined group-hover:scale-110 transition-transform">photo_camera</span>
            </button>
          </div>
          <textarea
            value={inventory}
            onChange={(e) => setInventory(e.target.value)}
            placeholder="Bijv: 'een halve courgette, een pak rijst en twee eieren...'"
            className="w-full h-32 bg-gray-50 dark:bg-black/20 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-primary transition-all resize-none"
          />
          <button
            onClick={onAnalyze}
            disabled={isAnalyzing || !inventory.trim()}
            className="w-full py-4 rounded-2xl bg-primary text-black font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50 transition-all"
          >
            <span className={`material-symbols-outlined ${isAnalyzing ? 'animate-spin' : 'filled'}`}>
              {isAnalyzing ? 'sync' : 'auto_awesome'}
            </span>
            {isAnalyzing ? 'Coach denkt na...' : 'Ontdek recepten'}
          </button>
        </section>

        {recommendation && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
            <div className="bg-primary/10 border border-primary/20 p-4 rounded-2xl flex gap-3">
              <span className="material-symbols-outlined text-primary">tips_and_updates</span>
              <p className="text-xs font-medium leading-relaxed italic">"{recommendation.coachTip}"</p>
            </div>

            {recommendation.type === 'existing' && recommendation.existingRecipeId && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Match uit je database</h3>
                {existingRecipes.filter(r => r.id === recommendation.existingRecipeId).map(recipe => (
                   <div 
                    key={recipe.id} 
                    onClick={() => onSelectRecipe(recipe.id)}
                    className="bg-white dark:bg-surface-dark rounded-3xl overflow-hidden border border-gray-100 dark:border-white/5 shadow-md flex"
                  >
                    <div className="w-1/3 aspect-square">
                      <img src={recipe.image} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4 flex flex-col justify-center">
                      <h4 className="font-bold text-base line-clamp-2">{recipe.title}</h4>
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400">
                        <span className="material-symbols-outlined text-xs">schedule</span>
                        {recipe.time}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {recommendation.newRecipe && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-primary uppercase tracking-widest flex items-center gap-2">
                    <span className={`material-symbols-outlined text-sm ${isRefining ? 'animate-spin' : 'filled'}`}>
                        {isRefining ? 'sync' : 'auto_awesome'}
                    </span>
                    {isRefining ? 'Coach past aan...' : (recommendation.type === 'new' ? 'Nieuwe creatie' : 'Aangepast recept')}
                </h3>
                <div className="bg-white dark:bg-surface-dark rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-md">
                  <h4 className="text-xl font-bold mb-2">{recommendation.newRecipe.title}</h4>
                  <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-white/10 rounded-lg text-gray-500">{recommendation.newRecipe.time}</span>
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-white/10 rounded-lg text-gray-500">{recommendation.newRecipe.category}</span>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Ingrediënten</p>
                      {recommendation.newRecipe.ingredients.map((ing: any, i: number) => {
                          const isAdded = addedIngredients.some(ai => ai.toLowerCase() === ing.name.toLowerCase());
                          return (
                            <div key={i} className={`flex justify-between text-xs py-2 border-b border-gray-50 dark:border-white/5 ${isAdded ? 'text-primary font-bold' : ''}`}>
                                <div className="flex items-center gap-2">
                                    {isAdded && <span className="material-symbols-outlined text-[14px] filled">check_circle</span>}
                                    <span>{ing.name}</span>
                                </div>
                                <span className="text-gray-400">{ing.amount}</span>
                            </div>
                          );
                      })}
                  </div>

                  <button 
                    onClick={handleSaveAndViewNewRecipe}
                    disabled={isPreparingRecipe || isRefining}
                    className="w-full py-4 rounded-xl bg-primary text-black font-bold text-sm shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-[20px]">visibility</span>
                    Bekijk dit recept
                  </button>
                </div>

                <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-4 border border-gray-100 dark:border-white/5 space-y-3">
                    <div className="flex items-center gap-2">
                         <span className="material-symbols-outlined text-primary text-[18px]">edit_note</span>
                         <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Verfijn dit recept</p>
                    </div>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={refinePrompt}
                            onChange={(e) => setRefinePrompt(e.target.value)}
                            placeholder="Bijv: 'maak het pittiger'..."
                            className="flex-1 bg-white dark:bg-black/20 border-none rounded-xl px-4 py-3 text-xs focus:ring-1 focus:ring-primary"
                        />
                        <button 
                            onClick={() => { onRefine(refinePrompt); setRefinePrompt(""); }}
                            disabled={isRefining || !refinePrompt.trim()}
                            className="bg-black dark:bg-white text-white dark:text-black px-5 py-3 rounded-xl text-xs font-bold disabled:opacity-50 transition-colors"
                        >
                            {isRefining ? '...' : 'Pas aan'}
                        </button>
                    </div>
                </div>
              </div>
            )}

            {recommendation.missingIngredients && recommendation.missingIngredients.filter((ing: any) => !addedIngredients.some(ai => ai.toLowerCase() === ing.name.toLowerCase())).length > 0 && (
              <section className="bg-accent-blue/5 border border-accent-blue/20 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-accent-blue">shopping_cart_checkout</span>
                      <h3 className="text-sm font-bold text-accent-blue">Maak het compleet</h3>
                  </div>
                  <span className="text-[10px] bg-accent-blue text-white px-2 py-0.5 rounded-full font-bold">TIP</span>
                </div>
                <div className="space-y-2">
                  {recommendation.missingIngredients.filter((ing: any) => !addedIngredients.some(ai => ai.toLowerCase() === ing.name.toLowerCase())).map((ing: Ingredient, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white/50 dark:bg-black/20 rounded-xl">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold">{ing.name}</span>
                        <span className="text-[10px] text-gray-400">{ing.amount}</span>
                      </div>
                      <button 
                        onClick={() => onAddIngredient(ing)}
                        disabled={isRefining}
                        className="size-9 rounded-full bg-accent-blue text-white flex items-center justify-center active:scale-90 transition-transform shadow-md disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-sm">add</span>
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 text-center italic">Toegevoegde items worden direct verwerkt in de bereiding.</p>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};